import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslatableListField } from '@/components/form/TranslatableListField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { listToInput, valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import {
  type DepartmentRow,
  type FacultyRow,
  PROGRAM_CAMPUSES,
  PROGRAM_LEVELS,
  type ProgramCampus,
  type ProgramInput,
  type ProgramLevel,
  type ProgramRow,
} from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/programs`.
 *
 * The catalogue is readable across faculties and writable only within one —
 * `ProgramPolicy` explains why: a Faculty Admin needs to see the whole thing so
 * as not to duplicate a programme another faculty already runs.
 *
 * The filter that matters most is not on the public page at all. §4 stores
 * `accreditation_until`, and a programme whose accreditation has lapsed cannot
 * legally admit students — so the list leads with that.
 */
export function ProgramsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams('sort_order');

  const faculties = useResourceList<FacultyRow>('faculties', { per_page: 100, sort: 'sort_order' });

  const columns: Column<ProgramRow>[] = [
    {
      key: 'name',
      label: t('academic.programs.columns.name'),
      render: (row) => (
        <div className="min-w-52">
          <span className="font-semibold text-primary">{valueFor(row.name, locale)}</span>

          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            {row.code ? <span className="text-caption text-muted">{row.code}</span> : null}
            {row.accreditation_expired ? (
              <Chip tone="gold">{t('academic.programs.accreditationExpired')}</Chip>
            ) : null}
          </span>
        </div>
      ),
    },
    {
      key: 'faculty',
      label: t('academic.departments.columns.faculty'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {row.faculty ? valueFor(row.faculty.name, locale) : '—'}
          {row.department ? ` · ${valueFor(row.department.name, locale)}` : ''}
        </span>
      ),
    },
    {
      key: 'level',
      label: t('academic.programs.columns.level'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {t(`academic.programs.levels.${row.level}`)}
          {' · '}
          {t(`academic.programs.campuses.${row.campus}`)}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'ects_total',
      label: t('academic.programs.columns.ects'),
      sortable: true,
      align: 'end',
      render: (row) => row.ects_total ?? '—',
    },
  ];

  return (
    <ResourceListScreen
      resource="programs"
      title={t('nav.items.programs')}
      intro={t('academic.programs.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.name, locale)}
      defaultSort="sort_order"
      emptyTitle={t('academic.programs.empty.title')}
      emptyBody={t('academic.programs.empty.body')}
      newLabel={t('academic.programs.new')}
      deleteTitle={t('academic.programs.delete.title')}
      deleteBody={(label) => t('academic.programs.delete.body', { label })}
      filters={
        <>
          <Select
            label={t('academic.departments.columns.faculty')}
            hideLabel
            value={String(list.params.faculty_id ?? '')}
            onChange={(event) => list.setFilter('faculty_id', event.target.value || null)}
            options={[
              { value: '', label: t('academic.anyFaculty') },
              ...(faculties.data?.data ?? []).map((faculty) => ({
                value: String(faculty.id),
                label: valueFor(faculty.name, locale),
              })),
            ]}
          />

          <Select
            label={t('academic.programs.columns.level')}
            hideLabel
            value={String(list.params.level ?? '')}
            onChange={(event) => list.setFilter('level', event.target.value || null)}
            options={[
              { value: '', label: t('academic.programs.anyLevel') },
              ...PROGRAM_LEVELS.map((level) => ({
                value: level,
                label: t(`academic.programs.levels.${level}`),
              })),
            ]}
          />

          {/*
            The one an editor needs and a reader has no equivalent of: a
            programme whose accreditation has lapsed cannot legally admit
            students.
          */}
          <Button
            type="button"
            size="sm"
            variant={list.params.accreditation_expiring ? 'primary' : 'secondary'}
            aria-pressed={Boolean(list.params.accreditation_expiring)}
            onClick={() =>
              list.setFilter(
                'accreditation_expiring',
                list.params.accreditation_expiring ? null : '1',
              )
            }
          >
            {t('academic.programs.accreditationRunningOut')}
          </Button>
        </>
      }
    />
  );
}

interface Fields {
  faculty_id: number | '';
  department_id: string;
  code: string;
  name: TranslatedValue;
  slug: TranslatedValue;
  level: ProgramLevel;
  campus: ProgramCampus;
  qualification: TranslatedValue;
  summary: TranslatedValue;
  body: TranslatedValue;
  learning_outcomes: TranslatedValue;
  admission_requirements: TranslatedValue;
  required_exams: Record<string, string[] | null>;
  ects_total: string;
  duration_years: string;
  language: TranslatedValue;
  tuition_fee: string;
  head_staff_id: string;
  is_accredited: boolean;
  accreditation_until: string;
  sort_order: number;
  status: ProgramRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can, hasRole, user } = useAuth();

  const mayPublish = can('programs.publish');
  const scoped = hasRole('faculty-admin');

  const faculties = useResourceList<FacultyRow>('faculties', { per_page: 100, sort: 'sort_order' });

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<ProgramRow, Fields, ProgramInput>({
      resource: 'programs',
      recordId,
      empty: () => empty(scoped ? (user?.faculty?.id ?? '') : ''),
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'faculty_id',
        'department_id',
        'code',
        'name',
        'slug',
        'level',
        'campus',
        'qualification',
        'summary',
        'body',
        'admission_requirements',
        'ects_total',
        'duration_years',
        'language',
        'tuition_fee',
        'head_staff_id',
        'accreditation_until',
        'sort_order',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  /*
   * Only this faculty's departments. The server refuses a department from
   * another faculty — the catalogue would otherwise render a programme under
   * one faculty's heading and another's department — so the selector never
   * offers one.
   */
  const departments = useResourceList<DepartmentRow>('departments', {
    per_page: 100,
    sort: 'sort_order',
    ...(values.faculty_id === '' ? {} : { faculty_id: Number(values.faculty_id) }),
  });

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">
            {isNew ? t('academic.programs.new') : t('academic.programs.edit')}
          </h1>

          {record?.accreditation_expired ? (
            <Chip tone="gold" className="mt-2">
              {t('academic.programs.accreditationExpired')}
            </Chip>
          ) : null}
        </div>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/programs')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('academic.programs.fields.name')}
            name="name"
            value={values.name}
            onChange={(next) => setValue('name', next, { shouldDirty: true })}
            error={formState.errors.name?.message}
          />

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record?.slug}
            published={values.status === 'published'}
            error={formState.errors.slug?.message}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('academic.departments.columns.faculty')}
              hint={scoped ? t('academic.lockedToYourFaculty') : undefined}
              {...register('faculty_id', { valueAsNumber: true })}
              disabled={scoped}
              error={formState.errors.faculty_id?.message}
              options={[
                { value: '', label: t('academic.chooseFaculty') },
                ...(faculties.data?.data ?? []).map((faculty) => ({
                  value: String(faculty.id),
                  label: valueFor(faculty.name, locale),
                })),
              ]}
            />

            <Select
              label={t('academic.programs.fields.department')}
              hint={t('academic.programs.fields.departmentHint')}
              {...register('department_id')}
              error={formState.errors.department_id?.message}
              options={[
                { value: '', label: t('academic.programs.noDepartment') },
                ...(departments.data?.data ?? []).map((department) => ({
                  value: String(department.id),
                  label: valueFor(department.name, locale),
                })),
              ]}
            />

            <Select
              label={t('academic.programs.columns.level')}
              {...register('level')}
              error={formState.errors.level?.message}
              options={PROGRAM_LEVELS.map((level) => ({
                value: level,
                label: t(`academic.programs.levels.${level}`),
              }))}
            />

            <Select
              label={t('academic.programs.fields.campus')}
              {...register('campus')}
              error={formState.errors.campus?.message}
              options={PROGRAM_CAMPUSES.map((campus) => ({
                value: campus,
                label: t(`academic.programs.campuses.${campus}`),
              }))}
            />

            <Input
              {...register('code')}
              label={t('academic.programs.fields.code')}
              hint={t('academic.programs.fields.codeHint')}
              error={formState.errors.code?.message}
            />

            <TranslatableField
              label={t('academic.programs.fields.qualification')}
              name="qualification"
              value={values.qualification}
              onChange={(next) => setValue('qualification', next, { shouldDirty: true })}
              required={false}
            />
          </div>

          <TranslatableField
            label={t('content.fields.summary')}
            hint={t('academic.programs.fields.summaryHint')}
            name="summary"
            multiline
            rows={3}
            value={values.summary}
            onChange={(next) => setValue('summary', next, { shouldDirty: true })}
            error={formState.errors.summary?.message}
          />

          <RichTextField
            label={t('content.fields.body')}
            value={values.body}
            onChange={(next) => setValue('body', next, { shouldDirty: true })}
          />

          {/* Rich text, not a list — §4 says so, and so does the public
              programme page. `required_exams` below is the list. */}
          <RichTextField
            label={t('academic.programs.fields.outcomes')}
            value={values.learning_outcomes}
            onChange={(next) => setValue('learning_outcomes', next, { shouldDirty: true })}
          />

          <RichTextField
            label={t('academic.programs.fields.admission')}
            value={values.admission_requirements}
            onChange={(next) => setValue('admission_requirements', next, { shouldDirty: true })}
          />

          <TranslatableListField
            label={t('academic.programs.fields.exams')}
            hint={t('academic.programs.fields.examsHint')}
            value={values.required_exams}
            onChange={(next) => setValue('required_exams', next, { shouldDirty: true })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              {...register('ects_total')}
              type="number"
              min={1}
              label={t('academic.programs.columns.ects')}
              hint={t('academic.programs.fields.ectsHint')}
              error={formState.errors.ects_total?.message}
            />

            <Input
              {...register('duration_years')}
              type="number"
              min={0.5}
              step="0.5"
              label={t('academic.programs.fields.duration')}
              error={formState.errors.duration_years?.message}
            />

            <Input
              {...register('tuition_fee')}
              type="number"
              min={0}
              label={t('academic.programs.fields.tuition')}
              hint={t('academic.programs.fields.tuitionHint')}
              error={formState.errors.tuition_fee?.message}
            />

            <TranslatableField
              label={t('academic.programs.fields.language')}
              name="language"
              value={values.language}
              onChange={(next) => setValue('language', next, { shouldDirty: true })}
              required={false}
            />

            <Input
              {...register('head_staff_id')}
              type="number"
              min={1}
              label={t('academic.programs.fields.head')}
              hint={t('academic.departments.fields.headHint')}
              error={formState.errors.head_staff_id?.message}
            />

            <Input
              {...register('sort_order', { valueAsNumber: true })}
              type="number"
              min={0}
              label={t('content.fields.order')}
              hint={t('content.fields.orderHint')}
            />
          </div>

          {/*
            §4's accreditation. A programme whose accreditation has lapsed
            cannot legally admit students, and the server refuses the flag
            without a date — so the pair is one block.
          */}
          <fieldset className="space-y-4 rounded-card border border-subtle p-4">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('academic.programs.accreditation')}
            </legend>

            <label className="flex items-center gap-2 text-body-sm text-primary">
              <input
                type="checkbox"
                {...register('is_accredited')}
                className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
              />
              {t('academic.programs.fields.accredited')}
            </label>

            <Input
              {...register('accreditation_until')}
              type="date"
              label={t('academic.programs.fields.accreditationUntil')}
              hint={t('academic.programs.fields.accreditationUntilHint')}
              error={formState.errors.accreditation_until?.message}
            />
          </fieldset>
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="programs"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record?.updated_at}
            updatedBy={record?.updated_by}
            pendingReview={record?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              name: { label: t('academic.programs.fields.name'), value: values.name },
              summary: { label: t('content.fields.summary'), value: values.summary },
              body: { label: t('content.fields.body'), value: values.body },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(facultyId: number | ''): Fields {
  return {
    faculty_id: facultyId,
    department_id: '',
    code: '',
    name: {},
    slug: {},
    level: 'bachelor',
    campus: 'akhaltsikhe',
    qualification: {},
    summary: {},
    body: {},
    learning_outcomes: {},
    admission_requirements: {},
    required_exams: {},
    ects_total: '',
    duration_years: '',
    language: {},
    tuition_fee: '',
    head_staff_id: '',
    is_accredited: false,
    accreditation_until: '',
    sort_order: 0,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: ProgramRow): Fields {
  return {
    faculty_id: row.faculty_id,
    department_id: row.department_id === null ? '' : String(row.department_id),
    code: row.code ?? '',
    name: row.name,
    slug: row.slug,
    level: row.level,
    campus: row.campus,
    qualification: row.qualification,
    summary: row.summary,
    body: row.body,
    learning_outcomes: row.learning_outcomes,
    admission_requirements: row.admission_requirements,
    required_exams: row.required_exams,
    // Strings in the form so an emptied number input is `''` rather than the
    // `NaN` a `valueAsNumber` register produces.
    ects_total: row.ects_total === null ? '' : String(row.ects_total),
    duration_years: row.duration_years === null ? '' : String(row.duration_years),
    language: row.language,
    tuition_fee: row.tuition_fee === null ? '' : String(row.tuition_fee),
    head_staff_id: row.head_staff_id === null ? '' : String(row.head_staff_id),
    is_accredited: row.is_accredited,
    accreditation_until: row.accreditation_until ?? '',
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): ProgramInput {
  return {
    faculty_id: Number(form.faculty_id),
    department_id: form.department_id === '' ? null : Number(form.department_id),
    code: form.code.trim() === '' ? null : form.code.trim(),
    name: form.name,
    slug: form.slug,
    level: form.level,
    campus: form.campus,
    qualification: form.qualification,
    summary: form.summary,
    body: form.body,
    learning_outcomes: form.learning_outcomes,
    admission_requirements: form.admission_requirements,
    required_exams: listToInput(form.required_exams),
    ects_total: form.ects_total === '' ? null : Number(form.ects_total),
    duration_years: form.duration_years === '' ? null : Number(form.duration_years),
    language: form.language,
    tuition_fee: form.tuition_fee === '' ? null : Number(form.tuition_fee),
    head_staff_id: form.head_staff_id === '' ? null : Number(form.head_staff_id),
    is_accredited: form.is_accredited,
    accreditation_until: form.accreditation_until === '' ? null : form.accreditation_until,
    sort_order: Number(form.sort_order),
    status: form.status,
    published_at: form.published_at,
    pending_review: mayPublish ? form.pending_review : true,
  };
}
